import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import CrossPlatformDropdown from '../../../components/CrossPlatformDropdown';
import LhDatePicker from '../../../components/DatePicker/LhDatePicker';
import LhTextArea from '../../../components/LhTextArea';
import LhTextInput from '../../../components/LhTextInput';
import { createActionPlanFormStyles } from '../../ActionPlan/styles/createActionPlanForm.styles';
import { useSiteEvalActionPlanForm } from '../hooks/useSiteEvalActionPlanForm';
import { useTheme } from '../../../theme';

interface SiteEvalActionPlanFormProps {
  data?: any;
  onSubmit?: (payload: any) => void;
  onCancel?: () => void;
  onFormSubmitReady?: (submitFn: () => Promise<any>, setErrorsFn?: (errors: { validationErrors?: Record<string, string>; submissionError?: any }) => void) => void;
  onFormDirty?: () => void;
}

// Label component for form fields
const Label: React.FC<{ text: string; required?: boolean; showInfo?: boolean }> = ({ 
  text, 
  required = false, 
  showInfo = false 
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return (
    <View style={[createActionPlanFormStyles.labelRow, { justifyContent: 'space-between' }]}> 
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={[createActionPlanFormStyles.labelText, isDark && { color: colors.text.primary }]}>{text}</Text>
      </View>
      {required ? <Text style={[createActionPlanFormStyles.required, isDark && { color: colors.text.secondary }]}>*Required</Text> : null}
    </View>
  );
};

/**
 * Site Evaluation Action Plan Form
 * Custom form specifically designed for creating action plans from Site Evaluation
 */
const SiteEvalActionPlanForm: React.FC<SiteEvalActionPlanFormProps> = ({
  data = {},
  onSubmit,
  onCancel,
  onFormSubmitReady,
  onFormDirty,
}) => {
  // Stable ref so wrappers don't recreate on every render
  const onFormDirtyRef = React.useRef(onFormDirty);
  React.useEffect(() => { onFormDirtyRef.current = onFormDirty; }, [onFormDirty]);
  const markDirty = React.useCallback(() => { onFormDirtyRef.current?.(); }, []);
  const { width: screenWidth } = useWindowDimensions();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // Force single-column layout on iOS for better mobile/iPad experience
  const isTablet = Platform.OS !== 'ios' && screenWidth >= 768;

  const {
    title,
    setTitle,
    selectedSource,
    selectedType,
    setSelectedType,
    selectedPriority,
    setSelectedPriority,
    selectedOwner,
    selectedAssignee,
    setSelectedAssignee,
    startDate,
    setStartDate,
    targetDate,
    setTargetDate,
    handleTargetDateChange,
    actionPlan,
    setActionPlan,
    notes,
    setNotes,
    frontlineInvolvement,
    setFrontlineInvolvement,
    selectedEmployee,
    setSelectedEmployee,
    employeeList,
    employeeLoading,
    loading,
    dateValidationError,
    submissionError,
    validationErrors,
    assigneeLoading,
    isCreateDisabled,
    sourceOptions,
    typeOptions,
    priorityOptions,
    ownerOption,
    assigneeList,
    handleSubmit,
    setValidationErrors,
    setSubmissionError,
  } = useSiteEvalActionPlanForm(data, onSubmit);

  // Keep a ref to the latest handleSubmit so we can provide a stable callback to parent
  const handleSubmitRef = React.useRef(handleSubmit);
  React.useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Expose the handleSubmit function and error-setting function to parent via callback
  React.useEffect(() => {
    if (onFormSubmitReady) {
      const stableSubmit = () => {
        const fn = handleSubmitRef.current;
        if (fn) {
          return fn();
        }
        return Promise.resolve(null as any);
      };

      const setErrors = (errors: { validationErrors?: Record<string, string>; submissionError?: any }) => {
        if (errors.validationErrors) {
          setValidationErrors(errors.validationErrors);
        }
        if (errors.submissionError) {
          setSubmissionError(errors.submissionError);
        }
      };

      onFormSubmitReady(stableSubmit, setErrors);
    }
  }, [onFormSubmitReady, setValidationErrors, setSubmissionError]);

  const formatDate = (d: Date | null) => {
    if (!d) return '--';
    try {
      const dt = new Date(d);
      return `${(dt.getMonth() + 1).toString().padStart(2, '0')}/${dt.getDate().toString().padStart(2,'0')}/${dt.getFullYear()}`;
    } catch (e) {
      return String(d);
    }
  };

  return (
    <View style={createActionPlanFormStyles.root}>
      {/* Error Banner */}
      {submissionError && (
        <View style={[createActionPlanFormStyles.errorBanner, isDark && { backgroundColor: colors.statusBackground.error }]}>
          <View style={createActionPlanFormStyles.errorBannerContent}>
            <MaterialIcons name="error" size={20} color="#D32F2F" />
            <View style={createActionPlanFormStyles.errorTextContainer}>
              <Text style={createActionPlanFormStyles.errorTitle}>{submissionError.title}</Text>
              <Text style={[createActionPlanFormStyles.errorMessage, isDark && { color: colors.text.primary }]}>{submissionError.message}</Text>
            </View>
          </View>
          <TouchableOpacity 
            onPress={() => setSubmissionError(null)}
            style={createActionPlanFormStyles.errorCloseButton}
          >
            <MaterialIcons name="close" size={20} color="#D32F2F" />
          </TouchableOpacity>
        </View>
      )}
      
      <View style={[createActionPlanFormStyles.card, isDark && { backgroundColor: colors.background.elevated }]}>
        {/* Row 1: Title, Source */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Title" required />
            <LhTextInput 
              placeholder="Action Plan" 
              value={title} 
              onChangeText={(v) => { setTitle(v); markDirty(); }}
              placeholderTextColor={isDark ? colors.text.disabled : '#6c757d'} 
            />
            {validationErrors?.title && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.title}</Text>}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <CrossPlatformDropdown
              childList={sourceOptions}
              label="Source"
              labelStyle={{ fontWeight: '400', fontSize: 12 }}
              placeholder={'Site Evaluation'}
              placeholderStyle={{ fontSize: 14 }}
              selection="single"
              setSelectedValue={() => { /* noop - readonly */ }}
              selectedValue={selectedSource}
              isDisabled={true}
              readOnly={true}
              disabledBackgroundColor="#DADADA"
            />
            {validationErrors?.source && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.source}</Text>}
          </View>
        </View>

        {/* Row 2: Type, Priority */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Type" required showInfo />
            <CrossPlatformDropdown
              childList={typeOptions}
              label={''}
              labelStyle={{ fontWeight: "400", fontSize: 12 }}
              placeholder="Action Type"
              placeholderStyle={{ fontSize: 14 }}
              selection="single"
              setSelectedValue={(val) => { setSelectedType(val as any); markDirty(); }}
              selectedValue={selectedType}
              disableSorting={true}
            />
            {validationErrors?.type && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.type}</Text>}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Priority" required showInfo />
            <CrossPlatformDropdown
              childList={priorityOptions}
              label={''}
              labelStyle={{ fontWeight: "400", fontSize: 12 }}
              placeholder="Select Priority"
              placeholderStyle={{ fontSize: 14 }}
              selection="single"
              setSelectedValue={(val) => { setSelectedPriority(val as any); markDirty(); }}
              selectedValue={selectedPriority}
              disableSorting={true}
            />
            {validationErrors?.priority && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.priority}</Text>}
          </View>
        </View>

        {/* Row 3: Action Plan Owner, Assignee */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <CrossPlatformDropdown
              childList={ownerOption}
              label="Action Plan Owner"
              labelStyle={{ fontWeight: '400', fontSize: 12 }}
              placeholder={'--'}
              placeholderStyle={{ fontSize: 14 }}
              selection="single"
              setSelectedValue={() => { /* noop - readonly */ }}
              selectedValue={selectedOwner}
              isDisabled={true}
              readOnly={true}
              disabledBackgroundColor="#DADADA"
            />
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Assignee" required showInfo />
            <CrossPlatformDropdown
              childList={assigneeList}
              label={''}
              labelStyle={{ fontWeight: '400', fontSize: 12 }}
              placeholder={
                assigneeLoading 
                  ? 'Loading...' 
                  : selectedAssignee?.displayText ?? 'Select Assignee'
              }
              placeholderStyle={{ fontSize: 14 }}
              selection="single"
              setSelectedValue={(val) => { setSelectedAssignee(val as any); markDirty(); }}
              selectedValue={selectedAssignee}
            />
            {validationErrors?.assignee && (
              <Text style={createActionPlanFormStyles.errorText}>{validationErrors.assignee}</Text>
            )}
          </View>
        </View>

        {/* Row 4: Start Date, Target Date */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Start Date" required />
            <LhDatePicker
              label={''}
              value={startDate}
              onChange={(v) => { setStartDate(v); markDirty(); }}
              placeholder="MM/DD/YYYY"
              labelStyle={{fontSize:12}}
              colorScheme={isDark ? 'dark' : 'light'}
              iconColor={isDark ? colors.text.primary : '#3A3A3A'}
              dismissOnOutsidePress={true}
            />
            {validationErrors?.start_date && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.start_date}</Text>}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Target Date" required />
            <LhDatePicker
              label={''}
              value={targetDate}
              onChange={(v) => { handleTargetDateChange(v); markDirty(); }}
              placeholder="MM/DD/YYYY"
              labelStyle={{fontSize:12}}
              colorScheme={isDark ? 'dark' : 'light'}
              iconColor={isDark ? colors.text.primary : '#3A3A3A'}
              dismissOnOutsidePress={true}
            />
            {validationErrors?.target_date && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.target_date}</Text>}
            {dateValidationError && <Text style={createActionPlanFormStyles.errorText}>{dateValidationError}</Text>}
          </View>
        </View>

        {/* Row 5: Action Plan and Notes side by side */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Action Plan" required />
            <LhTextArea
              placeholder="Describe the action plan..."
              value={actionPlan}
              onChangeText={(v) => { setActionPlan(v); markDirty(); }}
              numberOfLines={4}
              placeholderTextColor={isDark ? colors.text.disabled : "#6c757d"}
            />
            {validationErrors?.action_plan && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.action_plan}</Text>}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            <Label text="Notes" />
            <LhTextArea
              placeholder="Additional notes (optional)..."
              value={notes}
              onChangeText={(v) => { setNotes(v); markDirty(); }}
              numberOfLines={4}
              placeholderTextColor={isDark ? colors.text.disabled : "#6c757d"}
            />
            {validationErrors?.notes && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.notes}</Text>}
          </View>
        </View>

        {/* Frontline Employee Involvement Checkbox */}
        <View style={[createActionPlanFormStyles.checkboxRow, !isTablet && createActionPlanFormStyles.checkboxRowMobile]}>
          <TouchableOpacity
            onPress={() => { setFrontlineInvolvement((v) => !v); markDirty(); }}
            style={createActionPlanFormStyles.checkboxTouch}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: frontlineInvolvement }}
          >
            <MaterialIcons
              name={frontlineInvolvement ? "check-box" : "check-box-outline-blank"}
              size={20}
              color={frontlineInvolvement ? (isDark ? colors.brand.primary : "#034895") : (isDark ? colors.text.secondary : "#6B6B6B")}
            />
          </TouchableOpacity>

          <Text style={[createActionPlanFormStyles.checkboxLabel, isDark && { color: colors.text.primary }]}>Frontline Employee involvement</Text>
        </View>

        {/* Employee Dropdown - Show when frontline involvement is checked */}
        {frontlineInvolvement && (
          <View style={createActionPlanFormStyles.employeeSection}>
            <Label text="Employee Name" required showInfo />
            <CrossPlatformDropdown
              childList={employeeList}
              label={''}
              labelStyle={{ fontWeight: "400", fontSize: 12 }}
              placeholder={employeeLoading ? 'Loading...' : 'Select Employee'}
              placeholderStyle={{ fontSize: 14 }}
              selection="single"
              setSelectedValue={(val) => { setSelectedEmployee(val as any); markDirty(); }}
              selectedValue={selectedEmployee}
              placement="top"
            />
            {validationErrors?.employee && (
              <Text style={createActionPlanFormStyles.errorText}>{validationErrors.employee}</Text>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

export default SiteEvalActionPlanForm;